import _ from "lodash";
import { createSelector } from "reselect";
import { getUserPaymentAccountCode, getUserSelectedAccount, getUserSelectedAccountCode } from "../../app/appSelectors";
import Asset from "../Asset/Asset";
import {
    LITE_TRADE_ORDER_STATUSES,
    LITE_TRADE_POSITION_EFFECTS,
    TChartTimeframes,
    UPS_DOWNS_ASSETS_COUNT,
} from './liteTradeConstants';
import type { IMobileAppRootState } from "../../mobileAppCore/types/mobileAppState.t";
import type {
    ILiteTradeAsset,
    ILiteTradeAssets,
    ILiteTradeAssetSymbol,
    ILiteTradeCollections,
    ILiteTradeMarketData,
    ILiteTradeMarketDataCandle,
    ILiteTradeMarketDataCandleTimeItem,
    ILiteTradeMetric,
    ILiteTradePortfolio,
    ILiteTradePortfolioOrderStatus,
    ILiteTradePortfolioOrderType,
    ILiteTradeWidgets,
    ILiteTradeWidgetsItem,
    IMarketDataCandleWatchers,
    IMarketDataWatchers,
    IPortfolioOrderId
} from "./types/liteTradeReducer.t";
import { AssetFilledQuantity } from "../Asset/Asset.t";
import jsonParse from "../../utils/jsonParse";
import { getMarketDataCandleSymbolLastPriceParams } from './utils/getMarketDataCandleSymbolLastPriceParams';
import calcAssetFilledQuantity from "./utils/calcAssetFilledQuantity";

const moduleSelector = (state: IMobileAppRootState) => state.liteTrade || {} as IMobileAppRootState['liteTrade'];

export const getLiteTradeConfig = createSelector(moduleSelector, state => state.liteTradeConfig);
export const getLiteTradeConfigError = createSelector(moduleSelector, state => state.liteTradeConfigError);
export const getLiteTradeConfigIsLoading = createSelector(moduleSelector, state => state.liteTradeConfigIsLoading);

export const getLiteTradePingOk = createSelector(moduleSelector, state => state.liteTradePingOk);
export const getLiteTradePingError = createSelector(moduleSelector, state => state.liteTradePingError);
export const getLiteTradeIsPingLoading = createSelector(moduleSelector, state => state.liteTradePingIsLoading);

export const getLiteTradeSocketState = createSelector(moduleSelector, state => state.liteTradeSocketState);

export const getLiteTradeSession = createSelector(moduleSelector, state => state.liteTradeSession);
export const getLiteTradeSessionToken = createSelector(getLiteTradeSession, state => state?.sessionToken);
export const getLiteTradeIsLoggedIn = createSelector(getLiteTradeSession, state => !!state?.sessionToken);

export const getLiteTradeIsLoginLoading = createSelector(moduleSelector, state => state.isLoginLoading);
export const getLiteTradeLoginError = createSelector(moduleSelector, state => state.loginError);

export const getCollectionsIsLoadingSelector = createSelector(moduleSelector, state => state.collectionsIsLoading);
export const getAssetsIsLoadingSelector = createSelector(moduleSelector, state => state.assetsIsLoading);

export const getLiteTradeAllAccountsMetrics = createSelector(
    moduleSelector, state => state.metrics,
);

export const getLiteTradeAccountMetrics = createSelector(
    [moduleSelector, getUserSelectedAccountCode], (state, account): ILiteTradeMetric => state?.metrics[String(account)],
);

export const makeSelectorGetLiteTradeAccountMetrics = (account: string) => createSelector(
    moduleSelector, (state): ILiteTradeMetric => state.metrics[account],
);

export const getGetLiteTradeAccountMetrics = (state: IMobileAppRootState): ILiteTradeMetric => {
    const account = getUserSelectedAccountCode(state);
    return moduleSelector(state).metrics[account]
};

export const getPaymentAccountMetricsSelector = createSelector(
    [moduleSelector, getUserPaymentAccountCode], (state, account): ILiteTradeMetric => state?.metrics[account],
);

const defaultAccountPortfolioState: ILiteTradePortfolio = {
    positions: [],
    orders: [],
    account: null
}

export const getLiteTradeAccountPortfolio = createSelector(
    [moduleSelector, getUserSelectedAccountCode], (state, account): ILiteTradePortfolio => state.portfolios[String(account)] || defaultAccountPortfolioState,
);

export const getLiteTradeAccountPortfolioInitialized = createSelector(
    [moduleSelector, getUserSelectedAccountCode], (state, account): boolean => state.portfoliosInitialized[String(account)] || false,
);

export const getIsLiteTradeAccountPortfolioInited = createSelector(
    getLiteTradeAccountPortfolio, portfolio => !!portfolio.positions,
);

export const getLiteTradeAccountOrders = createSelector(
    getLiteTradeAccountPortfolio, state => state.orders || [],
);

export const getLiteTradePortfolioVisibleOrdersSelector = createSelector(
    getLiteTradeAccountOrders, (orders) =>
        orders
            .filter(item => item.legs[0].positionEffect === LITE_TRADE_POSITION_EFFECTS.OPEN)
            .filter(item => item.status === 'WORKING')
);

export const getLiteTradeAccountPositions = createSelector(
    getLiteTradeAccountPortfolio, state => state.positions || [],
);

export const getLiteTradePortfolioVisiblePositionsSelector = createSelector(
    getLiteTradeAccountPositions, (positions) =>
        positions.filter(item => item.quantity > 0)
);

export const makeSelectorGetLiteTradeAccountPositionInfo = (positionCode: string) => createSelector(
    getLiteTradeAccountPortfolio, state => state.positions?.find(item => item.positionCode === positionCode),
);

export const makeSelectorGetLiteTradeAccountOrderInfo = (orderId: IPortfolioOrderId) => createSelector(
    getLiteTradeAccountPortfolio, state => state.orders?.find(item => item.orderId.toString() === orderId),
);

export const makeSelectorGetLiteTradeAccountOrderProtectionOrderList = (orderId: IPortfolioOrderId) => createSelector(
    [makeSelectorGetLiteTradeAccountOrderInfo(orderId), getLiteTradeAccountOrders,], (orderInfo, orders) =>
        orders.filter(order =>
            orderInfo?.links?.find(link => link?.linkedClientOrderId === order?.clientOrderId)
        ),
);

export const makeSelectorGetLiteTradeAccountOrderProtectionOrderInfoByType = (orderId: IPortfolioOrderId, protectionOrderTypeToSearch: ILiteTradePortfolioOrderType) => createSelector(
    makeSelectorGetLiteTradeAccountOrderProtectionOrderList(orderId), protectionOrders =>
        protectionOrders.find(protectionOrder => protectionOrder?.type === protectionOrderTypeToSearch)
);

export const makeSelectorGetLiteTradeAccountPositionProtectionOrderList = (positionCode: ILiteTradePortfolioOrderStatus) => createSelector(
    [getLiteTradeAccountOrders], (orders) =>
        orders.filter(order =>
            order.status === LITE_TRADE_ORDER_STATUSES.WORKING && order.legs[0].positionCode === positionCode,
        ),
);

export const makeSelectorGetLiteTradeAccountPositionProtectionOrderInfoByType = (positionCode: ILiteTradePortfolioOrderStatus, protectionOrderTypeToSearch: ILiteTradePortfolioOrderType) => createSelector(
    makeSelectorGetLiteTradeAccountPositionProtectionOrderList(positionCode), (protectionOrders) =>
        protectionOrders.find(protectionOrder => protectionOrder.type === protectionOrderTypeToSearch)
);

export const makeSelectorGetLiteTradeAccountOrdersByAccountCode = (accountCode?: string) => createSelector(
    moduleSelector, state => state.portfolios[accountCode || '']?.orders,
);

export const getLiteTradeAssets = createSelector(
    moduleSelector, (state): ILiteTradeAssets => state.assets || [],
);

export const getLiteTradeAssetsBySymbol = createSelector(
    getLiteTradeAssets, assets => _.keyBy<ILiteTradeAsset>(assets, 'symbol')
);

export const makeSelectorGetLiteTradeAssetInfo = (symbol: ILiteTradeAssetSymbol) => createSelector(
    getLiteTradeAssets, state => state.find(item => item.symbol === symbol),
);

export const makeSelectorGetLiteTradeAssetInfoPricePrecision = (symbol: ILiteTradeAssetSymbol) => {
    const getLiteTradeAssetInfo = makeSelectorGetLiteTradeAssetInfo(symbol);
    return createSelector(getLiteTradeAssetInfo, (assetInfo) => -Math.log10(assetInfo?.info?.priceIncrement || 0.01));
}

export const makeSelectorGetAssetInstance = (symbol: ILiteTradeAssetSymbol) => createSelector(
    getLiteTradeAssets, state => {
        const assetParams = state.find(item => item.symbol === symbol);
        return assetParams && new Asset(assetParams);
    },
);

export const getAuthVerifyInProgress = createSelector(
    moduleSelector, (state): boolean => state.authVerifyInProgress
);

export const getLiteTradeMarketData = createSelector(
    moduleSelector, (state): ILiteTradeMarketData => state.marketData,
);

export const makeSelectLiteTradeMarketDataBySymbol = (symbol: ILiteTradeAssetSymbol) => createSelector(
    getLiteTradeMarketData, (marketData) => marketData[symbol],
);

export const getLiteTradeMarketDataCandle = createSelector(
    moduleSelector, (state): ILiteTradeMarketDataCandle => state.marketDataCandle,
);

export const makeSelectLiteTradeMarketDataCandleBySymbol = (symbol: string) => createSelector(
    getLiteTradeMarketDataCandle, (marketDataCandle): ILiteTradeMarketDataCandleTimeItem => marketDataCandle[symbol],
);

export const makeMarketDataCandleLastPriceParamsBySymbolSelector = (symbol: string) => createSelector(
    [
        getLiteTradeAssetsBySymbol,
        getLiteTradeMarketDataCandle, // MarketDataCandle последний, порядок важен для equalityCheck
    ], (assetsBySymbolMap, marketDataCandle) => {
        const asset = assetsBySymbolMap[symbol];
        return getMarketDataCandleSymbolLastPriceParams(marketDataCandle[symbol], asset);
    },
    {
        memoizeOptions: {
            equalityCheck: (prev, next) => {
                return prev?.[symbol] === next?.[symbol]
            },
            resultEqualityCheck: (prev, next) => {
                return prev?.diffAmount === next?.diffAmount &&
                    prev?.diffPercent === next?.diffPercent &&
                    prev?.close === next?.close
            },
        }
    }
);

export const getMarketDataWatchers = createSelector(
    moduleSelector, (state): IMarketDataWatchers => state.marketDataWatchers,
);

export const getMarketDataCandleWatchers = createSelector(
    moduleSelector, (state): IMarketDataCandleWatchers => state.marketDataCandleWatchers,
);

export const getLiteTradeCollectionsList = createSelector(
    moduleSelector, (state): ILiteTradeCollections => state.collections as ILiteTradeCollections,
);

export const getLiteTradeCollectionsFilteredMap = createSelector(
    [getLiteTradeCollectionsList, getLiteTradeAssetsBySymbol], (collectionsList, usersAssetsBySymbol) =>
        _.chain(collectionsList)
            .map((collection) => ({
                ...collection,
                assets: _.filter(collection.assets, (collectionAsset) =>
                    !!usersAssetsBySymbol[collectionAsset.symbol])
            }))
            .keyBy('collection_key')
            .value()
);

export const makeSelectLiteTradeFilteredCollectionByKey = (collectionKey: string) => createSelector(
    getLiteTradeCollectionsFilteredMap, state => state[collectionKey],
);

export const getLiteTradeWidgets = createSelector(
    moduleSelector, (state): ILiteTradeWidgets => state.widgets as ILiteTradeWidgets,
);

export const makeSelectLiteTradeWidgetByWidgetKeyAndCollectionKey = (widgetKey: ILiteTradeWidgetsItem["widget_key"], collectionKey: ILiteTradeWidgetsItem["collection_key"]) => createSelector(
    getLiteTradeWidgets, state =>
        _.find(state, (widget) => widget.widget_key === widgetKey && widget.collection_key === collectionKey) || {} as ILiteTradeWidgetsItem
);

export const getLiteTradeMarketFavoriteInProgress = createSelector(
    moduleSelector, (state): boolean => state.marketFavoriteInProgress,
);

export const getLiteTradeChartTimeframes = createSelector(
    moduleSelector, (state): TChartTimeframes => state.chart.timeframes,
);

export const getLiteTradeChartIsDataReady = createSelector(
    moduleSelector, (state): boolean => state.chart.dataReady,
);

type IGetAssetFilledQuantityReturn = (state: IMobileAppRootState, props: ILiteTradeAssetSymbol) => AssetFilledQuantity;

export const getAssetFilledQuantityBySymbol: IGetAssetFilledQuantityReturn = createSelector(
    [getLiteTradeAccountPortfolio, (state, symbol) => symbol], (portfolio, symbol) =>
        calcAssetFilledQuantity(symbol, portfolio.positions)
);

export const getUpsDownsCollectionIsLoading = createSelector(
    moduleSelector, (state) => state.collectionUpsDownsIsLoading,
);

export const getUpsDownsCollectionAssetsList = createSelector(
    moduleSelector, (state) => state.collectionUpsDownsAssetsList,
);

export const getUpsDownsCollectionAssetsListParsed = createSelector(
    [getUpsDownsCollectionAssetsList, getLiteTradeAssetsBySymbol], (assetsList, assetsBySymbol) => _.chain(assetsList)
        .filter(asset => !!assetsBySymbol[asset.symbol])
        .orderBy(['position'], ['asc'])
        .map((item) => ({ ...item, params: jsonParse(item.params) }))
        .value(),
);

export const getUpsDownsCollectionAssetsListUpSide = createSelector(
    getUpsDownsCollectionAssetsListParsed, (assetsList) => _.chain(assetsList)
        .filter(item => item.params?.side === 'up')
        .take(UPS_DOWNS_ASSETS_COUNT)
        .value()
);

export const getUpsDownsCollectionAssetsListDownSide = createSelector(
    getUpsDownsCollectionAssetsListParsed, (assetsList) => _.chain(assetsList)
        .filter(item => item.params?.side === 'down')
        .take(UPS_DOWNS_ASSETS_COUNT)
        .value()
);

export const getCloseAllPositionsIsLoading = createSelector(
    moduleSelector, (state) => state.closeAllPositionsInProgress,
);

export const getAccountPlParamsSelector = createSelector(
    [
        getLiteTradeAccountMetrics,
        getUserSelectedAccount
    ],
    (metrics, selectedAccount) => {
        if (selectedAccount && metrics) {
            const balance = metrics.balance + metrics.credit;
            const pl = selectedAccount.pl + metrics.openPL;
            const diffPl = Math.max(pl / balance * 100, -100) || 0;
            let precision = 0;
            if (diffPl < 10)
                precision = 2;
            return {
                pl,
                diffPl,
                precision,
                show: true
            }
        }
        return { show: false };
    },
    {
        memoizeOptions: {
            resultEqualityCheck: (prev, next) => {
                return prev.pl === next.pl && prev.diffPl === next.diffPl && prev.show === next.show;
            }
        }
    }
);

export const getUpdatePositionInProgress = createSelector(moduleSelector, state => state.updatePositionInProgress);
export const getUpdateOrderInProgress = createSelector(moduleSelector, state => state.updateOrderInProgress);

export const getDesktopTradeTableMode = createSelector(moduleSelector, state => state.desktopTradeTableMode);

export const getPositionPlLastSeenSignDataSelector = createSelector(moduleSelector, state =>
    state.positionPlLastSeenSigns
);

export const getIsChartDrawPanelOpened = createSelector(moduleSelector, state => state.isChartDrawPanelOpened);
